/**
 * Every call the built-in admin panel makes.
 *
 * ─── Why there is no admin-specific auth here ───────────────────────────────
 * This panel is a route inside the game client, so it is already signed in:
 * every request below goes through `core/network/api.ts`, which carries the
 * ordinary 15-minute access token and silently refreshes it on a 401.
 * `RolesGuard` + `@Roles('ADMIN')` on the backend is the real gate;
 * `canOpenAdmin()` on the client only decides whether to draw the link.
 *
 * `x-api-key` still exists and is honoured — MAD and other external systems
 * use it. Nothing here does.
 *
 * ─── The one path that is not under /admin ──────────────────────────────────
 * `meta*` below calls `/game-admin/meta`, not `/admin/meta`. The core meta
 * controller is `@Public()` + `AdminAccessGuard` and accepts only header-based
 * auth (`x-api-key`), so a browser JWT bounces off it. See
 * `backend/src/game/admin/game-meta-admin.controller.ts` for the whole story.
 */
import { api, API_BASE } from '../../core/network/api';
import type {
  AdminStats, AdminUserDetail, AdminUserRow, ApiKeyRow, CommissionsMap, CompanyInfo,
  CreatedApiKey, CurrencyCode, FaqEntry, GenerateInvoiceBody, InvoiceRow, KycRow,
  LegalPage, MetaIconSlot, MetaResponse, NewsRow, Paged, PaymentConfigSnapshot,
  PlayerEventPage, ServerStatus, SettingsMap, SocialLink, UsersQuery, WithdrawalPage,
} from './admin-api.types';

/** Drops undefined entries so an absent filter is not sent as `?status=undefined`. */
function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/**
 * Multipart POST.
 *
 * `api.post` sets `Content-Type: application/json` on the shared axios
 * instance, and sending that with a FormData body makes the browser omit the
 * multipart boundary — the server then sees a body it cannot parse and
 * `@UploadedFile()` is undefined. Passing `undefined` for the header lets axios
 * set it (boundary included) from the FormData itself.
 */
function upload<T>(path: string, field: string, file: File): Promise<T> {
  const body = new FormData();
  body.append(field, file);
  return api.post<T>(path, body, {
    headers: { 'Content-Type': undefined as unknown as string },
  });
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const getStats = () => api.get<AdminStats>('/admin/stats');
export const getServerStatus = () => api.get<ServerStatus>('/admin/server-status');

// ─── Users ────────────────────────────────────────────────────────────────────

export const listUsers = (q: UsersQuery = {}) =>
  api.get<Paged<AdminUserRow>>(`/admin/users${qs({ page: q.page, search: q.search, status: q.status })}`);

export const getUser = (id: string) => api.get<AdminUserDetail>(`/admin/users/${id}`);

export const banUser = (id: string, reason?: string) =>
  api.post<{ success: boolean }>(`/admin/users/${id}/ban`, { reason });

export const unbanUser = (id: string) =>
  api.post<{ success: boolean }>(`/admin/users/${id}/unban`);

export const setUserPassword = (id: string, password: string) =>
  api.post<{ success: boolean }>(`/admin/users/${id}/password`, { password });

export const deleteUser = (id: string) =>
  api.del<{ success: boolean }>(`/admin/users/${id}`);

/**
 * Credit grant, via the game-layer route: it takes a required reason and an
 * idempotency key, so a double-click or a retry is applied once. Core's
 * `/admin/users/:id/add-credits` stamps a fresh random reference every call.
 */
export const grantCredits = (
  id: string,
  body: { amount: number; currency: CurrencyCode; reason: string; idempotencyKey: string },
) => api.post<{ success: boolean; alreadyApplied: boolean; newBalance: number }>(
  `/game-admin/players/${id}/credits`, body,
);

export type PlayerRole = 'PLAYER' | 'ADMIN' | 'KYC_ADMIN';

export const createUser = (body: {
  email: string; username?: string; password: string; role?: PlayerRole; emailVerified?: boolean;
}) => api.post<{ id: string }>('/admin/users/create', body);

export const updatePlayer = (
  id: string,
  body: { username?: string; email?: string; role?: PlayerRole },
) => api.patch<{ success: boolean; changed: string[] }>(`/game-admin/players/${id}`, body);

/** A 60-second URL that signs the CALLING BROWSER in as the player. */
export const impersonateUser = (id: string) =>
  api.post<{ token: string; url: string }>(`/admin/users/${id}/impersonate`);

/**
 * Marks an account's email verified, and approves its KYC.
 *
 * Both live under `/game-admin`, not `/admin`, because core has neither: there
 * is no admin route for email verification at all, and `/admin/kyc/users/:id/
 * approve` refuses any account that is not already PENDING — which is every
 * account that never submitted. See
 * `backend/src/game/admin/game-admin-users.controller.ts`.
 */
export const verifyUserEmail = (id: string) =>
  api.post<{ success: boolean; alreadyVerified: boolean }>(`/game-admin/users/${id}/verify-email`);

export const verifyUserKyc = (id: string) =>
  api.post<{ success: boolean; alreadyVerified: boolean; reviewed: boolean }>(
    `/game-admin/users/${id}/verify-kyc`,
  );

export const listUserEvents = (userId: string, page = 1) =>
  api.get<PlayerEventPage>(`/admin/users/${userId}/events${qs({ page, pageSize: 25 })}`);

// ─── KYC ──────────────────────────────────────────────────────────────────────

export const listKyc = (q: { page?: number; status?: string } = {}) =>
  api.get<Paged<KycRow>>(`/admin/kyc/users${qs(q)}`);

export const approveKyc = (id: string) =>
  api.post<{ success: boolean }>(`/admin/kyc/users/${id}/approve`);

export const rejectKyc = (id: string, reason: string) =>
  api.post<{ success: boolean }>(`/admin/kyc/users/${id}/reject`, { reason });

// ─── Withdrawals ──────────────────────────────────────────────────────────────

export const listWithdrawals = (q: { page?: number; status?: string } = {}) =>
  api.get<WithdrawalPage>(`/admin/withdrawals${qs(q)}`);

export const approveWithdrawal = (id: string) =>
  api.post<{ success: boolean }>(`/admin/withdrawals/${id}/approve`);

export const rejectWithdrawal = (id: string, reason: string) =>
  api.post<{ success: boolean }>(`/admin/withdrawals/${id}/reject`, { reason });

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const listInvoices = (q: { page?: number; search?: string; days?: number } = {}) =>
  api.get<Paged<InvoiceRow>>(`/admin/invoices${qs(q)}`);

export const generateInvoice = (body: GenerateInvoiceBody) =>
  api.post<InvoiceRow>('/admin/invoices/generate', body);

/**
 * The download is a file response, not JSON, so it is a plain link rather than
 * an XHR — axios would hand back a blob this panel has no reason to hold.
 *
 * A link works here because the access token is a COOKIE in this stack
 * (`auth.controller.ts` sets it alongside the refresh cookie), not a header the
 * client attaches per request — so a same-origin navigation carries it. The one
 * case it does not cover is a token that expired since the last call: there is
 * no interceptor on a navigation, so the tab shows the backend's 401 instead of
 * refreshing and retrying. Reloading the panel fixes it, and turning invoice
 * PDFs into blob downloads to close that gap is a bigger change than this needs.
 */
export const invoiceDownloadUrl = (number: string) =>
  `${API_BASE}/admin/invoices/${encodeURIComponent(number)}/download`;

// ─── CMS: FAQ ─────────────────────────────────────────────────────────────────

export const listFaq = () => api.get<FaqEntry[]>('/admin/faq');
export const createFaq = (body: Partial<FaqEntry>) => api.post<FaqEntry>('/admin/faq', body);
export const updateFaq = (id: string, body: Partial<FaqEntry>) =>
  api.patch<FaqEntry>(`/admin/faq/${id}`, body);
export const deleteFaq = (id: string) => api.del<FaqEntry>(`/admin/faq/${id}`);

// ─── CMS: Legal ───────────────────────────────────────────────────────────────

export const listLegal = () => api.get<LegalPage[]>('/admin/legal');
export const createLegal = (body: Partial<LegalPage>) => api.post<LegalPage>('/admin/legal', body);
export const updateLegal = (id: string, body: Partial<LegalPage>) =>
  api.patch<LegalPage>(`/admin/legal/${id}`, body);
export const deleteLegal = (id: string) => api.del<LegalPage>(`/admin/legal/${id}`);

// ─── CMS: Social links ────────────────────────────────────────────────────────

export const listSocialLinks = () => api.get<SocialLink[]>('/admin/social-links');
export const createSocialLink = (body: Partial<SocialLink>) =>
  api.post<SocialLink>('/admin/social-links', body);
export const updateSocialLink = (id: string, body: Partial<SocialLink>) =>
  api.patch<SocialLink>(`/admin/social-links/${id}`, body);
export const deleteSocialLink = (id: string) => api.del<SocialLink>(`/admin/social-links/${id}`);

// ─── CMS: News ────────────────────────────────────────────────────────────────

export const listNews = (page = 1) => api.get<Paged<NewsRow>>(`/admin/news${qs({ page })}`);
export const createNews = (body: Partial<NewsRow>) => api.post<NewsRow>('/admin/news', body);
export const updateNews = (id: string, body: Partial<NewsRow>) =>
  api.patch<NewsRow>(`/admin/news/${id}`, body);
export const deleteNews = (id: string) => api.del<NewsRow>(`/admin/news/${id}`);
export const uploadNewsImage = (file: File) =>
  upload<{ url: string }>('/admin/news/upload-image', 'image', file);

// ─── Payments config ──────────────────────────────────────────────────────────

export const getPaymentsConfig = () =>
  api.get<PaymentConfigSnapshot>('/admin/payments/config');

export const updatePaymentsConfig = (body: Record<string, unknown>) =>
  api.patch<PaymentConfigSnapshot>('/admin/payments/config', body);

// ─── Settings ─────────────────────────────────────────────────────────────────

export const getSettings = () => api.get<SettingsMap>('/admin/settings');
export const setSetting = (key: string, value: string) =>
  api.post<{ ok: boolean }>('/admin/settings', { key, value });

export const getCompany = () => api.get<CompanyInfo>('/admin/settings/company');
export const setCompany = (body: Partial<CompanyInfo>) =>
  api.post<{ ok: boolean }>('/admin/settings/company', body);

export const getCommissions = () => api.get<CommissionsMap>('/admin/settings/commissions');
export const setCommissions = (body: Record<string, number>) =>
  api.post<{ ok: boolean }>('/admin/settings/commissions', body);

// ─── Meta tags ────────────────────────────────────────────────────────────────

export const getMeta = () => api.get<MetaResponse>('/game-admin/meta');

export const saveMeta = (settings: Record<string, string>) =>
  api.post<unknown>('/game-admin/meta', { settings });

export const uploadMetaIcon = (slot: MetaIconSlot, file: File) =>
  upload<unknown>(`/game-admin/meta/icons/${slot}`, 'file', file);

export const clearMetaIcon = (slot: MetaIconSlot) =>
  api.del<unknown>(`/game-admin/meta/icons/${slot}`);

export const uploadMetaImage = (file: File) =>
  upload<unknown>('/game-admin/meta/image', 'file', file);

export const clearMetaImage = () => api.del<unknown>('/game-admin/meta/image');

// ─── API keys ─────────────────────────────────────────────────────────────────

export const listApiKeys = () => api.get<ApiKeyRow[]>('/admin/api-keys');
export const createApiKey = (label: string) =>
  api.post<CreatedApiKey>('/admin/api-keys', { label });
export const deleteApiKey = (id: string) =>
  api.del<{ success: boolean }>(`/admin/api-keys/${id}`);
